import React, { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Card from "components/card";
import { MdArrowBack, MdDownload, MdFileDownload, MdError, MdTranslate } from "react-icons/md";
// import { QRCodeSVG } from "qrcode.react";
// import JSZip from "jszip";
import axios from "axios";
import { toast } from "react-hot-toast";

// Translations object
const translations = {
  en: {
    totalWeight: "Total Weight",
    originsSummary: "Origins Summary",
    detailedBatchList: "Detailed Batch List",
    size: "Size",
    weight: "Weight (kg)",
    origin: "Origin",
    noBatches: "No cashew batches",
    noBatchesDesc: "There are currently no batches listed in this zone.",
    loading: "Loading",
    pleaseWait: "Please wait...",
    goBack: "Go Back to Zones",
    lastUpdated: "Last Updated",
    zoneDetails: "Zone Details",
    oops: "Oops! Something went wrong"
  },
  pt: {
    totalWeight: "Peso Total",
    originsSummary: "Resumo das Origens",
    detailedBatchList: "Lista Detalhada de Lotes",
    size: "Tamanho",
    weight: "Peso (kg)",
    origin: "Origem",
    noBatches: "Sem lotes de castanha",
    noBatchesDesc: "Não há lotes listados nesta zona atualmente.",
    loading: "Carregando",
    pleaseWait: "Por favor, aguarde...",
    goBack: "Voltar para Zonas",
    lastUpdated: "Última Atualização",
    zoneDetails: "Detalhes da Zona",
    oops: "Ops! Algo deu errado"
  }
};

// Helper function to calculate total weight (can be moved to a utils file)
const calculateTotalWeight = (cashews) => {
  if (!cashews || !Array.isArray(cashews)) return 0;
  return cashews.reduce((total, cashew) => total + cashew.weight, 0);
};

// Helper function to get origins summary (can be moved to a utils file)
const getOriginsSummary = (cashews) => {
  if (!cashews || !Array.isArray(cashews)) return [];
  const origins = [...new Set(cashews.map(c => c.origin))];
  return origins.map(origin => ({
    origin,
    totalWeight: cashews
      .filter(c => c.origin === origin)
      .reduce((sum, c) => sum + c.weight, 0)
  }));
};

// Add a loading component
const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const ZoneDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { zoneId } = useParams();
  const [language, setLanguage] = useState('en');

  // Determine the zone name: prefer URL param, fall back to state
  const zoneName = zoneId || location.state?.zone?.name;

  const [zone, setZone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State to track bulk download progress
  // const [bulkDownloadProgress, setBulkDownloadProgress] = useState(0);
  // const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'pt' : 'en');
  };

  // Always fetch fresh data from the new API
  useEffect(() => {
    const fetchZoneData = async () => {
      if (!zoneName) {
        setError("No zone specified");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await axios.get("https://usibras-web-backend.onrender.com/api/stack-zone-data");

        const transformedZones = response.data.data.map((z, index) => ({
          id: index + 1,
          name: z.zone,
          totalWeight: z.total_weight,
          cashews: z.origins.flatMap(origin =>
            origin.sizes.map(sizeEntry => ({
              size: `Size ${sizeEntry.size.split('_')[1]}`,
              weight: sizeEntry.weight,
              origin: origin.origin
            }))
          )
        }));

        const foundZone = transformedZones.find(z => z.name === zoneName);
        if (foundZone) {
          setZone(foundZone);
        } else {
          setError("Zone not found");
        }
      } catch (err) {
        console.error('API Error:', err);
        setError(err.message || "Failed to fetch zone data");
      } finally {
        setLoading(false);
      }
    };

    fetchZoneData();
  }, [zoneName]);

  return (
    <div className="mt-5 h-full">
      {/* Language Toggle Button */}
      <button
        onClick={toggleLanguage}
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center rounded-full bg-brand-500 p-3 text-white shadow-lg hover:bg-brand-600 transition-all duration-200"
        aria-label="Toggle language"
      >
        <MdTranslate className="h-6 w-6" />
        <span className="ml-2 text-sm font-medium">{language.toUpperCase()}</span>
      </button>

      {loading ? (
        <Loader />
      ) : error ? (
        <div className="flex h-[400px] items-center justify-center">
          <div className="text-center">
            <div className="mb-4 text-5xl text-red-500">
              <MdError className="mx-auto h-16 w-16" />
            </div>
            <p className="text-xl font-semibold text-gray-700 dark:text-white">
              {translations[language].oops}
            </p>
            <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
            <button
              onClick={() => navigate('/admin/warehouse/consume')}
              className="mt-6 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-600 hover:shadow-lg"
            >
              {translations[language].goBack}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-5">
          <Card extra="w-full p-6">
            {/* Add 'no-print' class to header/buttons if needed */}
            <div className="mb-6 flex items-center justify-between border-b border-gray-200 pb-4 dark:border-navy-600 no-print">
              <div className="flex flex-col gap-2">
                 <div className="flex items-center gap-3">
                    <button
                       onClick={() => navigate('/admin/warehouse/consume')}
                       className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-navy-600"
                       aria-label="Go back to zones"
                    >
                       <MdArrowBack className="h-6 w-6 text-navy-700 dark:text-white" />
                    </button>
                    <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
                       {translations[language].zoneDetails} {zone.name}
                    </h2>
                 </div>
                 <div className="ml-12 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                       <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                       </svg>
                       {translations[language].totalWeight}: {zone.totalWeight?.toLocaleString() ?? calculateTotalWeight(zone.cashews)} kg
                    </span>
                 </div>
              </div>
              {/* QR Code Download Button - Commented out
              {zone.cashews && zone.cashews.length > 0 && (
                <button
                  onClick={downloadZoneQRCode}
                  disabled={isBulkDownloading}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md border border-transparent bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition duration-150 ease-in-out hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:bg-brand-400 dark:hover:bg-brand-300 dark:focus:ring-offset-navy-700 disabled:opacity-70"
                  aria-label="Download zone QR code"
                >
                  <MdFileDownload className="-ml-0.5 h-5 w-5" />
                  {isBulkDownloading 
                    ? "Generating..." 
                    : "Download QR Code"}
                </button>
              )}
              */}
            </div>

            {/* Details Content */}
            <div className="space-y-8"> {/* Increased spacing between sections */}
              {/* Total Weight */}
              <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-600">
                <h3 className="mb-2 text-lg font-semibold text-gray-700 dark:text-gray-300">
                  {translations[language].totalWeight}
                </h3>
                <p className="text-2xl font-bold text-brand-500">
                  {(zone.totalWeight ?? calculateTotalWeight(zone.cashews)).toLocaleString()} kg
                </p>
              </div>

              {/* Origins Summary */}
              <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-600">
                <h3 className="mb-4 text-lg font-semibold text-gray-700 dark:text-gray-300">
                  {translations[language].originsSummary}
                </h3>
                {getOriginsSummary(zone.cashews).length > 0 ? (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {getOriginsSummary(zone.cashews).map((origin) => (
                      <div
                        key={origin.origin}
                        className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700"
                      >
                        <p className="font-medium text-gray-700 dark:text-gray-300">
                          {origin.origin}
                        </p>
                        <p className="text-2xl font-bold text-brand-500">
                          {origin.totalWeight} kg
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                   <p className="text-sm text-gray-500 dark:text-gray-400">{translations[language].noBatches}</p>
                )}
              </div>

              {/* Detailed List - Updated Styling */}
              <div className="rounded-lg border border-gray-200 dark:border-navy-700"> {/* Keep border around the whole section */}
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-4 dark:border-navy-600 dark:bg-navy-800 no-print"> {/* Hide header on print */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                      {translations[language].detailedBatchList}
                    </h3>
                  </div>
                </div>

                {zone.cashews.length > 0 ? (
                  <div className="overflow-x-auto">
                    {/* Use a div for the table container to manage overflow */}
                    <div className="inline-block min-w-full align-middle">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-navy-600">
                        <thead className="no-print">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                              {translations[language].size}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                              {translations[language].weight}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                              {translations[language].origin}
                            </th>
                          </tr>
                        </thead>
                        {/* Apply zebra striping and hover effect to tbody */}
                        <tbody className="divide-y divide-gray-200 bg-white dark:divide-navy-600 dark:bg-navy-700">
                          {zone.cashews.map((cashew, index) => (
                            <tr
                              key={index}
                              className="hover:bg-gray-50 dark:hover:bg-navy-600"
                            >
                              <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                                {cashew.size}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                {cashew.weight}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                {cashew.origin}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  // Improved Empty State Styling
                  <div className="px-6 py-10 text-center">
                     <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                     </svg>
                     <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                       {translations[language].noBatches}
                     </h3>
                     <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                       {translations[language].noBatchesDesc}
                     </p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Hidden div to render the QR code for download - Commented out
      <div style={{ display: 'none' }} ref={qrCodeRef}>
        <QRCodeSVG 
          value={`https://usibras-web.vercel.app/admin/warehouse/zone-details/${zoneId}`}
          size={240} 
          level={"H"} 
          includeMargin={true} 
        />
      </div>
      */}
    </div>
  );
};

export default ZoneDetails; 